<script setup lang="ts" generic="T extends Record<string, boolean>">
import { computed, inject, nextTick, ref, watch, type ComponentPublicInstance } from 'vue';
import { default as NxTableColumn, type NxTableColumnProps } from './NxTableColumn.vue';
import { NxCheckbox, NxSelectInput } from '../Inputs';
import { i18nInjectionKey } from '../internal';
import {
  boolCellChecked,
  boolCellDisplay,
  boolCellValue,
  type NxTableBoolColumnVariant,
} from './boolCell';
import {
  defaultEditCellFocusRef,
  onEditCellKeydown,
  PartialOrNull,
  type EditCellRegisterFocus,
} from './internal';

const props = withDefaults(
  defineProps<
    Omit<NxTableColumnProps<T, keyof T, boolean>, 'toCsvString'> & {
      /** Defaults to the localized yes/no words used by table filters. */
      trueDisplayValue?: string;
      falseDisplayValue?: string;
      /** value that will be used for the csv export; If not provided the display-values will be used */
      toCsvString?: NxTableColumnProps<T, keyof T, boolean>['toCsvString'];
      /**
       * `select` (default): the display cell shows the yes/no word, the edit
       * cell a yes/no select. `checkbox`: both show a centred `NxCheckbox` —
       * a click or Space ticks it and commits once, the arrow keys, Enter and
       * Tab move on like in every other edit cell, and a cell that is not
       * editable for its row shows it disabled. `null` reads as unticked. The
       * filter and create row stay the yes/no select.
       */
      variant?: NxTableBoolColumnVariant;
    }
  >(),
  {
    filterable: true,
    resizeable: true,
    reorderable: true,
    sortable: true,
    editable: true,
    defaultVisible: true,
    layout: 'row',
    trueDisplayValue: undefined,
    falseDisplayValue: undefined,
    toCsvString: undefined,
    variant: 'select',
  }
);

const i18n = inject(i18nInjectionKey)!;
const trueDisplayValue = computed(
  () => props.trueDisplayValue ?? i18n.t('common.table.filter.bool_true')
);
const falseDisplayValue = computed(
  () => props.falseDisplayValue ?? i18n.t('common.table.filter.bool_false')
);

function toCsvString_(rowData: T, rowIndex: number) {
  if (props.toCsvString) {
    return props.toCsvString(rowData, rowIndex);
  }
  return displayOf(rowData);
}

/** The word for ONE row. Never cached on the column — see `boolCell.ts`. */
function displayOf(rowData: T): string {
  return boolCellDisplay(rowData[props.columnId], trueDisplayValue.value, falseDisplayValue.value);
}

/**
 * The select models an array, so the row's word is wrapped for it. Derived per
 * call rather than held in a ref: an inline-editable grid renders one edit cell
 * PER ROW at the same time, and a column-wide ref makes every one of them show
 * whichever row rendered last.
 */
function editModelOf(rowData: T): string[] {
  return [displayOf(rowData)];
}

const isCheckbox = computed(() => props.variant === 'checkbox');
/** The header says WHAT; a checkbox without a visible label still needs a name. */
const checkboxLabel = computed(() => props.displayName ?? String(props.columnId));

/**
 * The box of a checkbox edit cell as its focus target. `NxCheckbox` renders its
 * `role="checkbox"` button inside a wrapper, so the generic lookup of
 * `defaultEditCellFocusRef` would not find it. The box leaves the native Tab
 * order: the grid owns it (`onEditCellKeydown`), and since the cell stays
 * clickable while a sibling owns the cursor (`inactivePointerInteractive`), a
 * native Tab at the grid's edge would otherwise walk into the next row's box.
 * One function per cell, like `defaultEditCellFocusRef`, so a re-render does
 * not unregister and register the cell again.
 */
const checkboxFocusRefs = new WeakMap<EditCellRegisterFocus, (inst: unknown) => void>();
/** The box each edit cell currently renders, for `commitChecked`. */
const boxByCell = new WeakMap<EditCellRegisterFocus, HTMLElement>();
function checkboxFocusRef(registerFocus: EditCellRegisterFocus): (inst: unknown) => void {
  const cached = checkboxFocusRefs.get(registerFocus);
  if (cached) return cached;
  const focusRef = defaultEditCellFocusRef(registerFocus);
  const fn = (inst: unknown) => {
    const root = (inst as ComponentPublicInstance | null)?.$el as unknown;
    const box =
      root instanceof Element ? root.querySelector<HTMLElement>('[role="checkbox"]') : null;
    if (box) {
      box.tabIndex = -1;
      boxByCell.set(registerFocus, box);
    }
    focusRef(box);
  };
  checkboxFocusRefs.set(registerFocus, fn);
  return fn;
}

/**
 * Commit a tick and keep the keyboard on the box. While the save runs the
 * cell's `NxLoadingWrapper` disables the box, and the browser drops the focus
 * of a disabled button to `<body>`. The grid reads that as "focus left the
 * table": it clears its cursor, a `lazyEditCells` grid unmounts the row's
 * editors, and Space and the arrow keys stopped working after every tick
 * (I-3485). So the focus waits on the box's holder (`.bool-checkbox`, inside
 * the cell, answering the arrow keys like the box) while the save runs and
 * comes back to the box once it is through, unless the user went elsewhere.
 * A click moves the focus to the box too — WebKit does not focus a clicked
 * button by itself.
 */
function commitChecked(
  checked: boolean,
  onEdit: (newValues: Partial<T>) => Promise<void>,
  holder: HTMLElement | null
): Promise<void> {
  const box = holder?.querySelector<HTMLElement>('[role="checkbox"]') ?? null;
  holder?.focus({ preventScroll: true });
  return onEdit({ [props.columnId]: checked } as Partial<T>).finally(async () => {
    if (!holder) return;
    // The save status leaves "loading" through the wrapper's own watcher, a
    // render or two after the save settled: wait for the box to be enabled.
    for (let frame = 0; frame < RESTORE_FRAMES; frame++) {
      await nextTick();
      const now = document.activeElement;
      if (now !== null && now !== document.body && now !== holder) return;
      const target =
        box?.isConnected === true ? box : holder.querySelector<HTMLElement>('[role="checkbox"]');
      if (!target) return;
      if (!target.matches(':disabled')) {
        target.focus({ preventScroll: true });
        return;
      }
      await new Promise((resolve) => requestAnimationFrame(resolve));
    }
  });
}

/** How long `commitChecked` waits for the box to leave the save's disabled state. */
const RESTORE_FRAMES = 30;

/** The box's holder in the edit cell `registerFocus` belongs to. */
function holderOf(registerFocus: EditCellRegisterFocus): HTMLElement | null {
  return boxByCell.get(registerFocus)?.closest<HTMLElement>('.bool-checkbox') ?? null;
}

// One filter row and one create row exist per column, so a column-wide ref is
// correct for those two. The EDIT cell is the exception handled above.
const filterValue = ref<string[]>([]);
const createValue = ref<string[]>([]);
// Keep already selected words in sync when the locale or explicit labels change.
watch([trueDisplayValue, falseDisplayValue], ([yes, no], [previousYes, previousNo]) => {
  const translate = (value: string) =>
    value === previousYes ? yes : value === previousNo ? no : value;
  filterValue.value = filterValue.value.map(translate);
  createValue.value = createValue.value.map(translate);
});
/**
 * The filter-row select is modelled on the DISPLAY words. A loaded query hands
 * over booleans, the header-filter popover hands over the wire strings
 * `"true"` / `"false"` — both are mapped onto the words; anything else (an
 * already translated word set by the filter row itself) passes through.
 */
function onQueryLoaded(value: (boolean | string)[]) {
  filterValue.value = value.map((v) => {
    if (v === true || v === 'true') return trueDisplayValue.value;
    if (v === false || v === 'false') return falseDisplayValue.value;
    return v;
  });
}
</script>

<template>
  <!-- @vue-generic {T, keyof T, boolean} -->
  <NxTableColumn
    v-bind="{
      ...props,
      filterKind: props.filterKind ?? 'bool',
      onQueryLoaded: props.onQueryLoaded ? props.onQueryLoaded : onQueryLoaded,
      toCsvString: toCsvString_,
      // A checkbox is ticked with one click, also in a cell the cursor is not on.
      inactivePointerInteractive: props.inactivePointerInteractive || isCheckbox,
      // ... and in a row a lazy grid has not activated yet: the box is light, so
      // every row mounts it instead of a read-only stand-in (I-3485).
      eagerEditCell: props.eagerEditCell || isCheckbox,
    }"
  >
    <template v-if="$slots.summaryCell" #summaryCell="summaryProps">
      <slot name="summaryCell" v-bind="summaryProps"></slot>
    </template>
    <template v-if="$slots.headerCell" #headerCell>
      <slot name="headerCell"></slot>
    </template>
    <template
      #filterCell="{
        setFilterValue,
        disabled,
        registerFocus,
        focusAbove,
        focusBelow,
        focusLeft,
        focusRight,
      }"
    >
      <slot
        name="filterCell"
        v-bind="{
          setFilterValue,
          disabled,
          registerFocus,
          focusAbove,
          focusBelow,
          focusLeft,
          focusRight,
        }"
      >
        <NxSelectInput
          :ref="defaultEditCellFocusRef(registerFocus)"
          v-model="filterValue"
          arrow-keys-navigate
          height="xs"
          placeholder=""
          :disabled="disabled"
          :options="[trueDisplayValue, falseDisplayValue]"
          @update:model-value="
            (value: string[] | [null]) => {
              setFilterValue(value[0] === null ? [] : [value[0] === trueDisplayValue]);
            }
          "
          @keydown="
            (e: KeyboardEvent) =>
              onEditCellKeydown(e, {
                focusAbove,
                focusBelow,
                focusLeft,
                focusRight,
                inputType: 'select',
              })
          "
        ></NxSelectInput>
      </slot>
    </template>
    <template #cell="{ rowData, rowIndex }">
      <slot name="cell" v-bind="{ rowData, rowIndex }">
        <span v-if="isCheckbox" class="bool-checkbox read-only">
          <NxCheckbox
            :model-value="boolCellChecked(rowData[props.columnId])"
            :aria-label="checkboxLabel"
            disabled
          />
        </span>
        <span v-else class="one-line">
          {{ toCsvString_(rowData, rowIndex) }}
        </span>
      </slot>
    </template>
    <template #createCell="{ setPartialRowData }">
      <slot name="createCell" v-bind="{ setPartialRowData }">
        <NxSelectInput
          v-model="createValue"
          arrow-keys-navigate
          :placeholder="`${props.displayName ?? (props.columnId as string)}`"
          :options="[trueDisplayValue, falseDisplayValue]"
          @update:model-value="
            (value: string[] | [null]) => {
              setPartialRowData({
                [props.columnId]: value[0] !== null ? value[0] === trueDisplayValue : null,
              } as PartialOrNull<T>);
            }
          "
        ></NxSelectInput>
      </slot>
    </template>
    <template
      #editCell="{
        rowData,
        rowIndex,
        onEdit,
        registerFocus,
        focusAbove,
        focusBelow,
        focusLeft,
        focusRight,
      }"
    >
      <slot
        name="editCell"
        v-bind="{
          rowData,
          rowIndex,
          onEdit,
          registerFocus,
          focusAbove,
          focusBelow,
          focusLeft,
          focusRight,
        }"
      >
        <div v-if="isCheckbox" class="nx-ghost-cell">
          <!-- The holder takes the focus while a save disables the box
               (`commitChecked`); it answers the keys like the box. -->
          <span
            class="bool-checkbox"
            tabindex="-1"
            @keydown.self="
              (e: KeyboardEvent) => {
                // No second tick while the first one saves, and no page scroll.
                if (e.key === ' ') e.preventDefault();
                onEditCellKeydown(e, {
                  focusAbove,
                  focusBelow,
                  focusLeft,
                  focusRight,
                  inputType: 'select',
                });
              }
            "
          >
            <NxCheckbox
              :ref="checkboxFocusRef(registerFocus)"
              :model-value="boolCellChecked(rowData[props.columnId])"
              :aria-label="checkboxLabel"
              @update:model-value="
                (checked: boolean) => commitChecked(checked, onEdit, holderOf(registerFocus))
              "
              @keydown="
                (e: KeyboardEvent) =>
                  onEditCellKeydown(e, {
                    focusAbove,
                    focusBelow,
                    focusLeft,
                    focusRight,
                    inputType: 'select',
                  })
              "
            />
          </span>
        </div>
        <div v-else class="nx-ghost-cell">
          <NxSelectInput
            :ref="defaultEditCellFocusRef(registerFocus)"
            arrow-keys-navigate
            :model-value="editModelOf(rowData)"
            :placeholder="`${props.displayName ?? (props.columnId as string)}`"
            :options="[trueDisplayValue, falseDisplayValue]"
            @update:model-value="
              (value: string[] | [null]) => {
                // The placeholder stays null rather than collapsing to false —
                // clearing a nullable flag and setting it to no are different
                // writes. `boolCellValue` is the one place that decides.
                onEdit({
                  [props.columnId]: boolCellValue(value[0], trueDisplayValue),
                } as Partial<T>);
              }
            "
            @keydown="
              (e: KeyboardEvent) =>
                onEditCellKeydown(e, {
                  focusAbove,
                  focusBelow,
                  focusLeft,
                  focusRight,
                  inputType: 'select',
                })
            "
          ></NxSelectInput>
        </div>
      </slot>
    </template>
  </NxTableColumn>
</template>

<style scoped>
/* The box sits centred in the cell at its own size: the ghost cell stretches
   its child to the cell width (shell.css), the box itself must not grow, and
   it adds no frame or height of its own to the row. */
.bool-checkbox {
  display: flex;
  align-items: center;
  justify-content: center;
  min-width: 0;
  line-height: 1;
}

/* The holder only bridges a save (`commitChecked`); the box shows the focus. */
.bool-checkbox:focus {
  outline: none;
}

/* A read-only box only shows the value. Clicks pass through to the cell, which
   activates the row as for any other display cell — a disabled button would
   otherwise swallow them. */
.bool-checkbox.read-only {
  pointer-events: none;
}

.one-line {
  min-width: 0;
  overflow: hidden;
  white-space: nowrap;
  /* Cut long values with an ellipsis, never mid-letter. */
  text-overflow: ellipsis;
}
</style>
